import * as React from 'react'

import {
  BorderRadiusCorners,
  Box,
  Icon,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import DefaultShiftForm from 'modules/shift_management/lib/forms/DefaultShiftForm'
import UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'

import MultiSelector from './MultiSelector'
import SingleSelector from './SingleSelector'
import Wrapper from './Wrapper'

const borderRadius: BorderRadiusCorners = {
  all: 'abs008',
}

interface Props {
  form: UnassignedShiftForm | DefaultShiftForm
  newVersion?: boolean
}

const ShiftAccessSelectors = ({ form, newVersion = false }: Props) => {
  const isUnassignedShiftForm = form instanceof UnassignedShiftForm
  const isDefaultShiftForm = form instanceof DefaultShiftForm
  const field = form.get('all_company')
  const errors = field.errors || []
  const error = errors[0]

  const [open, toggle] = useToggle(false)
  const [selectedAllCompany, setSelectedAllCompany] = React.useState<boolean>(
    form.get('all_company').value
  )
  const [selectedTeamOrWorkplace, setSelectedTeamOrWorkplace] = React.useState<boolean>(
    !form.get('all_company').value
  )

  const [{ canCreateUnassignedShiftsForEverybody }] = usePermissions()
  const showAllCompanySelector =
    isDefaultShiftForm || (isUnassignedShiftForm && canCreateUnassignedShiftsForEverybody)

  const isFormComplete =
    !!form.get('all_company').value ||
    !!form.get('locations_ids_visibilities').value.length ||
    !!form.get('teams_ids_visibilities').value.length

  const onSelectAllCompany = () => {
    setSelectedAllCompany(true)
    setSelectedTeamOrWorkplace(false)
    form.get('all_company').set(true)
    form.get('locations_ids_visibilities').set('')
    field.setErrors([])
  }

  const onSelectTeamOrLocation = () => {
    setSelectedAllCompany(false)
    setSelectedTeamOrWorkplace(true)
    form.get('all_company').set(false)
    field.setErrors([])
  }

  const title = i18n.t('shift_management.coverage_section.access.title')
  const descriptionAllCompany = i18n.t('shift_management.coverage_section.access.all_company')
  const descriptionTeam = newVersion
    ? i18n.t('shift_management.coverage_section.access.team_or_workplace_new')
    : i18n.t('shift_management.coverage_section.access.team_or_workplace')

  return (
    <>
      <Box gap="s8" className={error && 'error-anchor'}>
        {!newVersion && (
          <Box flexDirection="row" gap="s4" alignItems="center">
            <Text.Normal size="200" weight="medium" color="grey1000">
              {title}:
            </Text.Normal>
            {!isFormComplete && (
              <Tooltip.Alt
                attachment="top center"
                open={open}
                toggle={toggle}
                label={i18n.t('common.missing_field')}
              >
                <Icon.Small icon="alert" encircled="grey800" />
              </Tooltip.Alt>
            )}
          </Box>
        )}

        {showAllCompanySelector && (
          <Wrapper
            description={descriptionAllCompany}
            isSelected={selectedAllCompany}
            onSelected={onSelectAllCompany}
            newVersion={newVersion}
          />
        )}

        <Wrapper
          description={descriptionTeam}
          isSelected={selectedTeamOrWorkplace}
          onSelected={onSelectTeamOrLocation}
          newVersion={newVersion}
        >
          {selectedTeamOrWorkplace && (
            <Box gap="s16" borderRadius={borderRadius} padding="s16">
              {isUnassignedShiftForm ? (
                <SingleSelector form={form} />
              ) : (
                <MultiSelector form={form} />
              )}
            </Box>
          )}
        </Wrapper>

        {error && (
          <Box alignItems="flexStart">
            <Text size="200" color="radicalRed1000" capitalize>
              {error}
            </Text>
          </Box>
        )}
      </Box>
    </>
  )
}

export default observer(ShiftAccessSelectors)
